import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CheckCircleOutlineIcon from '@atlaskit/icon/core/check-circle';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { mcpPermissionsMessages } from './messages';

const cardStyle = xcss({
  borderRadius: 'radius.xxlarge',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  padding: 'space.200',
});

const iconStyle = xcss({ flexShrink: 0, paddingBlock: 'space.025' });

type ForgeMcpPermissionSummaryProps = {
  appName: string;
  intentLabels: string[];
  includesIrreversible?: boolean;
};

/** Read-only summary shown when every Forge MCP toolset is selected. */
const ForgeMcpPermissionSummary: FunctionComponent<
  ForgeMcpPermissionSummaryProps
> = ({ appName, intentLabels, includesIrreversible = false }) => {
  const { formatList } = useIntl();

  return (
    <Box xcss={cardStyle}>
      <Inline space="space.150" alignBlock="start">
        <Box xcss={iconStyle} aria-hidden>
          <CheckCircleOutlineIcon
            label=""
            size="medium"
            color={token('color.text')}
          />
        </Box>
        <Box>
          <Text as="p" size="medium">
            <FormattedMessage
              {...mcpPermissionsMessages.forgeAppSummary}
              values={{
                appName: <strong>{appName}</strong>,
                intents: formatList(intentLabels, { type: 'conjunction' }),
              }}
            />
          </Text>
          {includesIrreversible && (
            <Text as="p" size="small" color="color.text.subtle">
              <FormattedMessage
                {...mcpPermissionsMessages.forgeAppIrreversibleNote}
              />
            </Text>
          )}
        </Box>
      </Inline>
    </Box>
  );
};

export default ForgeMcpPermissionSummary;
